import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import PropTypes from "prop-types";
import { fetchTaskDependenciesSuggestions, createTasksDependency } from "~/store/actions/actions.taskDependencies.jsx";
import { makeGetAllGroupedSuggestionsByTaskIdSelector } from "~/store/selectors/selector.taskDependencies.jsx";
import { getProjectIdByTaskId, makeGetTaskListIdByTaskIdSelector } from "~/store/selectors/selector.tasks.jsx";
import Angie from "~/utils/angie.jsx";
import AddDependencyToTaskTrigger from "./AddDependencyToTaskTrigger.jsx";
import DependencyForm from "./DependencyForm.jsx";

const DependencyFormContainer = ({ taskId, force, handleTriggerClick, ...rest }) => {
  const dispatch = useDispatch();
  const [fetching, setFetching] = useState(true);
  const signalRef = useRef();
  const getAllGroupedSuggestionsByTaskIdSelector = useMemo(makeGetAllGroupedSuggestionsByTaskIdSelector, []);
  const getTaskListIdByTaskIdSelector = useMemo(makeGetTaskListIdByTaskIdSelector, []);

  const tasks = useSelector((state) => getAllGroupedSuggestionsByTaskIdSelector(state, taskId));
  const projectId = useSelector((state) => getProjectIdByTaskId(state, taskId));
  const taskListId = useSelector((state) => getTaskListIdByTaskIdSelector(state, taskId));

  const getData = useCallback(async () => {
    // eslint-disable-next-line
    signalRef.current = new AbortController();
    const { signal } = signalRef.current;
    await dispatch(fetchTaskDependenciesSuggestions(taskId, signal));
  }, [dispatch, taskId]);

  useEffect(() => {
    getData().then(() => {
      setFetching(false);
    });

    return () => {
      signalRef.current.abort();
    };
  }, [getData]);

  const handleSubmit = useCallback(
    (attributes) => {
      Angie.logger.info("Task dependency created", {
        created_from: attributes.created_from,
        dependency_type: attributes.dependency_type,
      });
      const { created_from, dependency_type, ...rest } = attributes;
      dispatch(createTasksDependency(taskId, rest)).then(() => {
        dispatch(fetchTaskDependenciesSuggestions(taskId));
      });
    },
    [dispatch, taskId]
  );

  return force ? (
    <DependencyForm
      {...rest}
      taskId={taskId}
      tasks={tasks}
      projectId={projectId}
      taskListId={taskListId}
      fetching={fetching}
      onSubmit={handleSubmit}
      handleCancel={handleTriggerClick}
    />
  ) : (
    <AddDependencyToTaskTrigger
      handleOnClick={handleTriggerClick}
      projectId={projectId}
    />
  );
};

DependencyFormContainer.propTypes = {
  taskId: PropTypes.number,
  force: PropTypes.bool,
  handleTriggerClick: PropTypes.func,
};

DependencyFormContainer.defaultProps = {
  force: false,
};

export default DependencyFormContainer;
